import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Wallet } from 'lucide-react';
import { Button } from '@/shared/ui/buttons/Button';
import { Dialog } from '@/shared/ui/dialogs/Dialog';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { ChargeStatusChip } from '@/shared/ui/chips/ChargeStatusChip';
import { useMarkChargePaid, useMarkChargeUnpaid } from '@/entities/charges';
import { usePerm } from '@/shared/hooks/usePerm';
import { useToast } from '@/shared/hooks/useToast';
import { formatUsd } from '@/shared/utils/currency';
import type { ApiCharge, ApiChargeReceipt } from '@/shared/types/api/charges';

type Props = {
  charges: ApiCharge[];
  isLoading?: boolean;
};

export const FeaturesChargesTable = ({ charges, isLoading }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const canManageCharges = usePerm('charges.manage');
  const markPaid = useMarkChargePaid();
  const markUnpaid = useMarkChargeUnpaid();
  const [payTarget, setPayTarget] = useState<ApiCharge | null>(null);
  const [paidSuccess, setPaidSuccess] = useState<{
    receipt: ApiChargeReceipt | null;
  } | null>(null);

  const chargeColumns = useMemo<ColumnDef<ApiCharge>[]>(() => {
    const cols: ColumnDef<ApiCharge>[] = [
      {
        accessorKey: 'type_label',
        header: t('cargoIds.detail.charges.columns.type'),
        cell: (info) => (
          <span className="font-medium text-ink">{info.row.original.type_label}</span>
        ),
      },
      {
        accessorKey: 'description',
        header: t('cargoIds.detail.charges.columns.description'),
        cell: (info) => {
          const charge = info.row.original;
          return (
            <span className="text-ink-soft">
              {charge.related_parcel_display ||
                charge.related_package_display ||
                charge.description ||
                '—'}
            </span>
          );
        },
      },
      {
        accessorKey: 'amount',
        header: t('cargoIds.detail.charges.columns.amount'),
        cell: (info) => (
          <span className="font-medium">{formatUsd(Number(info.row.original.amount))}</span>
        ),
      },
      {
        accessorKey: 'is_paid',
        header: t('cargoIds.detail.charges.columns.status'),
        cell: (info) => {
          const charge = info.row.original;
          return <ChargeStatusChip amount={Number(charge.amount)} isPaid={charge.is_paid} />;
        },
      },
    ];

    if (canManageCharges) {
      cols.push({
        id: 'actions',
        header: t('cargoIds.detail.charges.columns.actions'),
        enableSorting: false,
        cell: (info) => {
          const charge = info.row.original;
          // $0.00 is FREE — no mark-paid / mark-unpaid action.
          if (Number(charge.amount) === 0) return null;
          return charge.is_paid ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={markUnpaid.isPending}
              onClick={(e) => {
                e.stopPropagation();
                markUnpaid.mutate(charge.id, {
                  onSuccess: () =>
                    toast.success(t('cargoIds.detail.charges.markUnpaidToast')),
                  onError: () => toast.error(t('common.errorGeneric')),
                });
              }}
            >
              {t('cargoIds.detail.charges.markUnpaid')}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="success"
              onClick={(e) => {
                e.stopPropagation();
                setPayTarget(charge);
              }}
            >
              {t('cargoIds.detail.charges.markPaid')}
            </Button>
          );
        },
      });
    }

    return cols;
  }, [t, toast, canManageCharges, markUnpaid]);

  return (
    <>
      <DataTable
        data={charges}
        columns={chargeColumns}
        isLoading={isLoading}
        emptyTitle={t('cargoIds.detail.charges.emptyTitle')}
        emptyIcon={<Wallet className="h-8 w-8" aria-hidden />}
        getRowId={(row) => String(row.id)}
      />

      <Dialog
        open={payTarget !== null}
        onClose={() => setPayTarget(null)}
        title={t('cargoIds.detail.charges.markPaidTitle')}
        description={
          payTarget
            ? t('cargoIds.detail.charges.markPaidDescription', {
                type: payTarget.type_label,
                amount: formatUsd(Number(payTarget.amount)),
              })
            : ''
        }
        size="md"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setPayTarget(null)}
              disabled={markPaid.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="success"
              loading={markPaid.isPending}
              onClick={() => {
                if (!payTarget) return;
                markPaid.mutate(payTarget.id, {
                  onSuccess: (data) => {
                    setPayTarget(null);
                    setPaidSuccess({ receipt: data.receipt });
                  },
                  onError: () => toast.error(t('common.errorGeneric')),
                });
              }}
            >
              {t('cargoIds.detail.charges.markPaidConfirm')}
            </Button>
          </>
        }
      >
        <div />
      </Dialog>

      <Dialog
        open={paidSuccess !== null}
        onClose={() => setPaidSuccess(null)}
        title={t('cargoIds.detail.charges.paidSuccessTitle')}
        description={t('cargoIds.detail.charges.paidSuccessBody')}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setPaidSuccess(null)}>
              {t('common.close')}
            </Button>
            {paidSuccess?.receipt && (
              <Button
                variant="primary"
                onClick={() => {
                  const url = paidSuccess.receipt?.public_url;
                  if (url) window.open(url, '_blank', 'noopener');
                }}
              >
                {t('cargoIds.detail.charges.viewReceipt')}
              </Button>
            )}
          </>
        }
      >
        <div />
      </Dialog>
    </>
  );
};
